import { Component, DoCheck, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import * as moment from 'moment';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { filter, map, pluck, skip, switchMap, take, takeUntil } from 'rxjs/operators';
import { LmoFracFctvTrailerTypeContractEditComponent } from '~lmo/components';
import { LmoFracFctvTrailerTypeContractComponent } from '~lmo/components/lmo-frac-fctv-trailer-type-contract/lmo-frac-fctv-trailer-type-contract.component';
import * as fromRouterConstants from '~lmo/lmo-routing.constants';
import { FracClusterTruckingVendor } from '~lmo/models/frac.model';
import { ResponseTrailerTypeContract } from '~lmo/models/trailerTypeContract.model';
import {
  FracClusterTruckingVendorUpdate,
  TruckingVendor,
  TruckingVendorContract,
} from '~lmo/models/trucking-vendor.model';
import { LmoTrailerTypeContractService, LmoTruckingVendorsService } from '~lmo/services';
import { fctvSorter, LmoFracFctvsService } from '~lmo/services/lmo-frac-fctvs.service';
import { LmoFracModalService } from '~lmo/services/lmo-frac-modal.service';
import { LmoFracsService } from '~lmo/services/lmo-fracs.service';
import { RouterStateService } from '~services/router-state.service';

interface UpdateFormValue {
  contract: TruckingVendorContract;
  dates: [Date, Date];
  description: string;
  distributions: { allocationPercent: number; fracClusterTruckingVendorId }[];
  vendorId: number;
}

@Component({
  selector: 'sa-lmo-frac-fctv-manage-id',
  styleUrls: ['./lmo-frac-fctv-manage-id.component.scss'],
  templateUrl: './lmo-frac-fctv-manage-id.component.html',
})
export class LmoFracFctvManageIdComponent implements OnInit, OnDestroy, DoCheck {
  @ViewChild('confirmCancel', { static: true }) private confirmCancel: TemplateRef<any>;
  @ViewChild('confirmDeleteTrailerTypeContract', { static: true })
  private confirmDeleteTrailerTypeContract: TemplateRef<any>;
  private destroy$: Subject<boolean> = new Subject<boolean>();
  public contractForm: FormControl = new FormControl();
  public contractManagement$$ = new BehaviorSubject<boolean>(false);
  public updateForm: FormGroup;
  public inNetworkVendors$: Observable<TruckingVendor[]>;
  public fctv$: Observable<FracClusterTruckingVendor>;
  public fctvs$: Observable<FracClusterTruckingVendor[]>;
  public vendor$: Observable<{ id: number }>;
  public vendorContracts$: Observable<TruckingVendorContract[]>;
  public vendorIdToName$: Observable<Record<number, string>>;
  public selectedVendorId$ = new BehaviorSubject<number>(null);
  public fracClusterTruckingVendorId$ = new BehaviorSubject<number>(null);
  public selectedToDelete$ = new BehaviorSubject<ResponseTrailerTypeContract>(null);
  public trailerTypeContracts$: Observable<ResponseTrailerTypeContract[]>;
  public listOfTrailerTypeId$ = new BehaviorSubject<number[]>(null);
  public maxDate = moment()
    .add(30, 'days')
    .toDate();
  public cancelButtonText$: Observable<string>;
  public get distributions(): FormArray {
    return this.updateForm.get('distributions') as FormArray;
  }

  public get updateFormValue(): UpdateFormValue {
    return this.updateForm.value;
  }

  constructor(
    private fb: FormBuilder,
    private fracFctvsService: LmoFracFctvsService,
    private truckingVendorService: LmoTruckingVendorsService,
    private fracHomeModalService: LmoFracModalService,
    private trailerTypeContractService: LmoTrailerTypeContractService,
    private routerState: RouterStateService,
    private matDialog: MatDialog,
    private fracService: LmoFracsService,
  ) {}

  public ngOnInit() {
    this.routerState.listenForParamChange$(fromRouterConstants.FCTV_ID).subscribe((fracClusterTruckingVendorId) => {
      this.fracClusterTruckingVendorId$.next(+fracClusterTruckingVendorId);
    });
    this.fctv$ = this.fracFctvsService.currentFctv$;
    this.vendor$ = this.fctv$.pipe(
      map((fctv) => ({
        id: fctv.truckingVendorId,
      })),
    );
    this.cancelButtonText$ = this.fctv$.pipe(
      map((fctv) => (fctv.status === 'awarded' ? 'Remove Vendor' : 'Cancel Request')),
    );
    this.fctvs$ = this.fracFctvsService.currentFracFctvs$;
    this.vendorContracts$ = this.truckingVendorService.vendorContracts$;
    this.inNetworkVendors$ = this.truckingVendorService.inNetwork$;
    this.vendorContracts$ = this.truckingVendorService.vendorContracts$;
    this.trailerTypeContracts$ = this.trailerTypeContractService.trailerTypeContracts$;
    this.vendorIdToName$ = this.fctvs$.pipe(
      map((fctvs) => {
        return fctvs.reduce((record, fctv) => {
          record[fctv.id] = fctv.truckingVendorName;
          return record;
        }, {});
      }),
    );
    this.setupContractDropDownListeners();
    this.setupForm();
  }

  public ngOnDestroy(): void {
    this.destroy$.next(true);
    // Just in case
    this.destroy$.unsubscribe();
  }

  public ngDoCheck(): void {
    this.updateListOfTrailerTypeId$();
  }

  public vendorDisplayFn(vendor?: TruckingVendor | TruckingVendorContract): string | undefined {
    return vendor ? vendor.name : undefined;
  }

  public createNewContract() {
    this.contractForm.setValue(null, { emitEvent: false });
    this.contractManagement$$.next(true);
  }

  public cancelContractChoice() {
    this.contractManagement$$.next(false);
  }

  public getVendorNameFromId$(id: number): Observable<string> {
    return this.vendorIdToName$.pipe(pluck(`${id}`));
  }

  public doUpdate() {
    if (this.updateForm.invalid) {
      return;
    }
    this.fctv$
      .pipe(
        take(1),
        switchMap((fctv) => {
          const formValue = this.updateFormValue;
          const allocationPercents: {
            fracClusterTruckingVendorId: number;
            allocationPercent: number;
          }[] = formValue.distributions.map((distribution) => ({
            allocationPercent: distribution.allocationPercent,
            fracClusterTruckingVendorId: distribution.fracClusterTruckingVendorId,
          }));
          const fracClusterTruckingVendor: FracClusterTruckingVendorUpdate = {
            description: formValue.description,
            endTimestamp: formValue.dates[1].toISOString(),
            startTimestamp: formValue.dates[0].toISOString(),
            vendorContractId: formValue.contract.id,
          };
          return this.fracFctvsService.updateFctv$(fctv.id, { allocationPercents, fracClusterTruckingVendor });
        }),
      )
      .subscribe((successful: boolean) => {
        if (successful) {
          this.fracHomeModalService.closeHomeModal();
        }
      });
  }

  public cancel() {
    const dialogRef = this.matDialog.open(this.confirmCancel, {
      disableClose: true,
      maxWidth: '968px',
      minWidth: '300px',
      width: '30%',
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.fctv$
          .pipe(
            take(1),
            switchMap((fctv) => {
              return this.fracFctvsService.rejectFctv$(fctv.id, fctv.status);
            }),
          )
          .subscribe((successful: boolean) => {
            if (successful) {
              this.fracHomeModalService.closeHomeModal();
            }
          });
      }
    });
  }

  private setupForm() {
    combineLatest([
      this.fctv$.pipe(filter((fctv) => !!fctv)),
      this.fctvs$.pipe(filter((fctvs) => !!fctvs && fctvs.length > 0)),
    ])
      .pipe(take(1))
      .subscribe(([fctv, fctvs]) => {
        this.fctvUpdateHandler(fctv);
        this.fctvsUpdateHandler(fctvs);
        this.setupContractListener();
        this.selectedVendorId$.next(fctv.truckingVendorId);
        this.trailerTypeContractService.loadTrailerTypeContractsFor(
          this.fracClusterTruckingVendorId$.value,
          this.selectedVendorId$.value,
        );
      });

    // Listen for FCTV changes, skipping the first one because it will be handled in the combination above
    this.fctv$
      .pipe(
        filter((fctv) => !!fctv),
        skip(1),
        takeUntil(this.destroy$),
      )
      .subscribe((fctv) => {
        this.fctvUpdateHandler(fctv);
      });

    // Listen for FCTV distribution changes changes, skipping the first one because it will be handled in the combination above
    this.fctvs$
      .pipe(
        filter((fctvs) => !!fctvs),
        skip(1),
        takeUntil(this.destroy$),
      )
      .subscribe((fctvs) => {
        this.fctvsUpdateHandler(fctvs);
      });
  }

  private fctvUpdateHandler(fctv: FracClusterTruckingVendor) {
    this.fracService.currentFrac$
      .pipe(
        filter((frac) => !!frac),
        take(1),
      )
      .subscribe((frac) => {
        this.truckingVendorService.loadPossibleContractsFor(fctv.truckingVendorId, frac.id);
      });
    const fakeContract = {
      id: fctv.vendorContractId,
      name: fctv.vendorContractName,
    };
    this.contractForm.setValue(fakeContract, { emitEvent: false });
    if (this.updateForm) {
      this.updateForm.patchValue({
        contract: fakeContract,
        dates: [new Date(fctv.startTimestamp), new Date(fctv.endTimestamp)],
        description: fctv.description,
      });
    } else {
      this.updateForm = this.fb.group({
        contract: [fakeContract, [Validators.required]],
        dates: [[new Date(fctv.startTimestamp), new Date(fctv.endTimestamp)], [Validators.required]],
        description: [fctv.description, []],
        distributions: this.fb.array([]),
      });
    }

    this.trailerTypeContractService.loadTrailerTypeContractsFor(fctv.id, fctv.truckingVendorId);
    this.fracClusterTruckingVendorId$.next(fctv.id);
  }

  private fctvsUpdateHandler(fctvs: FracClusterTruckingVendor[]) {
    fctvSorter(fctvs)
      .filter((f) => f.status === 'awarded' || f.status === 'pending')
      .reverse()
      .forEach((f) => {
        this.upsertDistributionPercentage(f.id, f.allocationPercent);
      });
  }

  private setupContractDropDownListeners() {
    this.contractForm.valueChanges.pipe(filter((contract) => !!contract)).subscribe((contract) => {
      this.contractManagement$$.next(true);
    });
  }

  private setupContractListener() {
    this.updateForm
      .get('contract')
      .valueChanges.pipe(filter((contract) => !!contract))
      .subscribe((value) => {
        this.contractManagement$$.next(false);
        this.contractForm.setValue(value, { emitEvent: false });
      });
  }

  private upsertDistributionPercentage(vendorId: number, percentage: number) {
    const index = this.updateFormValue.distributions.findIndex((dist) => dist.fracClusterTruckingVendorId === vendorId);
    if (index === -1) {
      this.distributions.insert(
        0,
        this.fb.group({
          allocationPercent: [percentage, [Validators.required, Validators.min(0), Validators.max(100)]],
          fracClusterTruckingVendorId: [vendorId, [Validators.required]],
        }),
      );
    } else {
      this.distributions.at(index).patchValue({
        allocationPercent: percentage,
      });
    }
  }

  public openCreateTrailerTypeContract() {
    const dataToPass = {
      fracClusterTruckingVendorId: this.fracClusterTruckingVendorId$.value,
      listOfTrailerTypeId: this.listOfTrailerTypeId$.value,
    };
    this.matDialog.open(LmoFracFctvTrailerTypeContractComponent, {
      data: dataToPass,
      width: '600px',
    });
  }

  public async openEditTrailerTypeContract(data: ResponseTrailerTypeContract) {
    await this.matDialog
      .open(LmoFracFctvTrailerTypeContractEditComponent, {
        data: data,
        width: '600px',
      })
      .afterClosed()
      .toPromise();
  }

  public openDeleteConfirmation(data: ResponseTrailerTypeContract) {
    this.selectedToDelete$.next(data);
    const dialogRef = this.matDialog.open(this.confirmDeleteTrailerTypeContract, {
      disableClose: true,
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.trailerTypeContractService.removeTrailerTypeContract$(data).subscribe(async () => {});
      }
    });
  }

  private updateListOfTrailerTypeId$() {
    const list: number[] = [];
    this.trailerTypeContracts$.subscribe((itemList) => {
      itemList.forEach((item) => {
        list.push(item.trailerTypeId);
      });
    });
    this.listOfTrailerTypeId$.next(list);
  }
}
